import type { BasemapConfig } from "@/types/layers";

// Reserved system layer key for the in-app Task overlay. This is not an ArcGIS layer,
// but we keep a row in the asset_layers table so admins can control its display_order
// alongside other layers. Never delete this key.
export const TASK_LAYER_KEY = "task_layer" as const;
export const TASK_LAYER_LABEL = "Task Layer" as const;

// Default rendering color for the task layer. Stored on `asset_layers.color`
// for the system task_layer row; admins can override per tenant. Kept in one
// place so the migration default, frontend renderers, and color picker
// fallback never drift apart.
export const DEFAULT_TASK_LAYER_COLOR = "#FF0000" as const;
export const DEFAULT_TASK_LAYER_COLOR_RGBA = [255, 0, 0, 255] as [
  number,
  number,
  number,
  number,
];

// Task layer MapLibre IDs - used for rendering task geometries on the map
export const TASK_SOURCE_ID = "task-layer-source" as const;
export const TASK_POINT_LAYER_ID = "task-layer-point" as const;
export const TASK_LINE_LAYER_ID = "task-layer-line" as const;
export const TASK_POLYGON_LAYER_ID = "task-layer-polygon" as const;

// Array of all task layer IDs for iteration
export const TASK_LAYER_IDS = [
  TASK_POLYGON_LAYER_ID,
  TASK_LINE_LAYER_ID,
  TASK_POINT_LAYER_ID,
] as const;

// Key used to persist the selected basemap in localStorage
export const BASEMAP_STORAGE_KEY = "taskr-selected-basemap" as const;

// Default fallback basemaps - used only if database is unavailable
// In normal operation, basemaps are loaded from the database via BasemapService
export const basemaps: BasemapConfig[] = [
  {
    key: "carto_positron",
    label: "Carto Positron",
    type: "vector",
    styleUrl: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
    attribution: "&copy; <a href='https://carto.com/attributions'>CARTO</a>",
  },
  {
    key: "carto_darkmatter",
    label: "Carto Dark Matter",
    type: "vector",
    styleUrl:
      "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
    attribution: "&copy; <a href='https://carto.com/attributions'>CARTO</a>",
  },
  {
    key: "osm_vector",
    label: "OpenStreetMap",
    type: "vector",
    styleUrl: "/styles/osm_raster_style.json",
    attribution: "&copy; OpenStreetMap contributors",
  },
  {
    key: "esri_imagery",
    label: "Esri World Imagery",
    type: "vector",
    styleUrl: "/styles/esri_imagery_style.json",
    attribution:
      "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
  },
  {
    key: "usgs_topo",
    label: "USGS Topo",
    type: "vector",
    styleUrl: "/styles/usgs_topo_style.json",
    attribution:
      "Tiles courtesy of the <a href='https://usgs.gov/'>U.S. Geological Survey</a>",
  },
  {
    key: "white_background",
    label: "White Background",
    type: "vector",
    styleUrl: "/styles/white_style.json",
    attribution: "None",
  },
];
